import { z } from 'zod';
import { IsoDateTime, Pence, Uuid } from '../primitives.js';

/**
 * WS9 — vouchers & gift cards on a double-entry liability ledger (brief 09 §2; ADR-0009 + ADR-0008).
 *
 * "Accounting is the point" (Overview §3.5): a gift-card SALE is a liability, never revenue; revenue
 * (and VAT) is recognised only at REDEMPTION, decrementing the liability — so a voucher can never count
 * twice (the Slick double-count bug). A voucher's BALANCE and the salon's OUTSTANDING LIABILITY are
 * NEVER stored as a mutable number — they are always the signed sum of append-only ledger entries.
 *
 * VAT (ADR-0008): a multi-purpose voucher (MPV, the default) carries NO VAT at sale (the sale receipt
 * line is `voucher_sale` + vatExempt → 0 bps, £0 revenue); VAT lands at redemption on the real lines.
 * A single-purpose voucher (SPV, `purpose='single'`) has VAT due at ISSUE; redemption is then VAT-
 * neutral. Money is integer pence; redeemable amounts are VAT-INCLUSIVE.
 *
 * REQUIRES ACCOUNTANT SIGN-OFF before go-live: the MPV/SPV classification and the breakage-VAT
 * treatment at expiry are tax judgements (ADR-0008 §"REQUIRES ACCOUNTANT SIGN-OFF"). The platform
 * records the decision; it does not, and must not, give tax advice.
 */

// ── Enums ────────────────────────────────────────────────────────────────────

/** A gift card (a redeemable monetary instrument) or a face-value voucher. */
export const VoucherKind = z.enum(['gift_card', 'voucher']);
export type VoucherKind = z.infer<typeof VoucherKind>;

/**
 * VAT class, frozen at issue (ADR-0008 §1, immutable per instance). `multi` = MPV (no VAT at sale,
 * VAT at redemption); `single` = SPV (VAT at issue, redemption VAT-neutral). Default `multi`, because a
 * general salon gift card is redeemable across services AND retail at differing rates → MPV by law.
 */
export const VoucherPurpose = z.enum(['multi', 'single']);
export type VoucherPurpose = z.infer<typeof VoucherPurpose>;

export const VoucherStatus = z.enum(['active', 'expired', 'void']);
export type VoucherStatus = z.infer<typeof VoucherStatus>;

/** The double-entry accounts a ledger entry can post to (ADR-0009). */
export const VoucherAccount = z.enum([
  'gift_card_liability',
  'cash',
  'card',
  'revenue',
  'vat_output',
  'breakage_income',
  'comp_expense',
]);
export type VoucherAccount = z.infer<typeof VoucherAccount>;

export const LedgerDirection = z.enum(['debit', 'credit']);
export type LedgerDirection = z.infer<typeof LedgerDirection>;

/** The business event a balanced set of ledger rows records (groups them by `event_id`). */
export const VoucherEventKind = z.enum(['sale', 'comp_issue', 'redemption', 'expiry', 'void']);
export type VoucherEventKind = z.infer<typeof VoucherEventKind>;

// ── Voucher (entity) ──────────────────────────────────────────────────────────

/**
 * One issued instrument. The current `balancePence` is DERIVED (signed sum of this voucher's
 * gift_card_liability entries), never stored — so the balance can never drift from the ledger.
 */
export const Voucher = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable().describe('the book that sold it; null for a shared/floor sale'),
    code: z.string().describe('opaque, high-entropy redemption code (unique per salon)'),
    kind: VoucherKind,
    purpose: VoucherPurpose,
    status: VoucherStatus,
    originalAmountPence: Pence.describe('face value at issue (VAT-inclusive); always > 0'),
    /** DERIVED current balance = signed sum of this voucher's gift_card_liability ledger entries. */
    balancePence: Pence,
    issuedAt: IsoDateTime,
    expiresAt: IsoDateTime,
    createdByStaffId: Uuid.nullable(),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'Voucher' });
export type Voucher = z.infer<typeof Voucher>;

/** One append-only journal row. A correction is a NEW reversing event, never an edit. */
export const VoucherLedgerEntry = z
  .object({
    id: Uuid,
    salonId: Uuid,
    renterId: Uuid.nullable(),
    voucherId: Uuid,
    eventId: Uuid.describe('groups the balanced rows of one transaction'),
    eventKind: VoucherEventKind,
    account: VoucherAccount,
    direction: LedgerDirection,
    amountPence: Pence.describe('always > 0; the sign is carried by `direction`'),
    paymentId: Uuid.nullable(),
    receiptId: Uuid.nullable(),
    refundId: Uuid.nullable(),
    reason: z.string().nullable().describe('actor-supplied reason for comp/void (audit trail)'),
    createdByStaffId: Uuid.nullable().describe('the acting staff member (no silent balance edits)'),
    createdAt: IsoDateTime,
  })
  .meta({ id: 'VoucherLedgerEntry' });
export type VoucherLedgerEntry = z.infer<typeof VoucherLedgerEntry>;

// ── Requests ─────────────────────────────────────────────────────────────────

/**
 * Issue (sell or comp) a voucher. `tender` distinguishes a SALE (cash/card in → gift_card_liability)
 * from a COMP (comp_expense → gift_card_liability, no money in). Idempotent on `idempotencyKey`.
 */
export const IssueVoucher = z
  .object({
    kind: VoucherKind.default('gift_card'),
    purpose: VoucherPurpose.default('multi'),
    amountPence: Pence.refine((v) => v > 0, 'amount must be > 0'),
    /** SALE = money in (debit cash/card); COMP = gifted balance (debit comp_expense, no money in). */
    tender: z.enum(['cash', 'card', 'comp']).default('card'),
    clientId: Uuid.nullish(),
    /** Required for a comp (the audit reason); optional otherwise. */
    reason: z.string().trim().min(1).max(500).optional(),
    /** Override the default 24-month expiry (never below the issued-at floor). */
    expiresAt: IsoDateTime.optional(),
    idempotencyKey: z.string().min(8).max(200),
  })
  .meta({ id: 'IssueVoucher' });
export type IssueVoucher = z.infer<typeof IssueVoucher>;

/**
 * Redeem against a voucher's balance — full or partial. `redeemedLines` are the REAL services/products
 * tendered against (VAT-inclusive), so the VAT engine recognises output VAT on the actual supply (MPV)
 * or marks them already-accounted (SPV). `amountPence` is the total redeemed and must equal Σ redeemed
 * line gross. Idempotent on `idempotencyKey`: a replay moves no total.
 */
export const RedeemVoucherLine = z
  .object({
    serviceId: Uuid.nullish(),
    productId: Uuid.nullish(),
    description: z.string().min(1).max(200),
    qty: z.int().min(1).default(1),
    unitPricePence: Pence.describe('VAT-inclusive unit price of the redeemed line'),
    performedByStaffId: Uuid.nullish(),
  })
  .meta({ id: 'RedeemVoucherLine' });
export type RedeemVoucherLine = z.infer<typeof RedeemVoucherLine>;

export const RedeemVoucher = z
  .object({
    /** Total amount to redeem (VAT-inclusive); must equal Σ redeemedLines gross and be ≤ balance. */
    amountPence: Pence.refine((v) => v > 0, 'amount must be > 0'),
    redeemedLines: z.array(RedeemVoucherLine).min(1),
    clientId: Uuid.nullish(),
    paymentId: Uuid.nullish().describe('the till payment this redemption tenders against, if any'),
    idempotencyKey: z.string().min(8).max(200),
  })
  .meta({ id: 'RedeemVoucher' });
export type RedeemVoucher = z.infer<typeof RedeemVoucher>;

// ── Read views ───────────────────────────────────────────────────────────────

/** A voucher with its full ledger (staff/till view — floor-visible, ids included). */
export const VoucherWithLedger = Voucher.extend({
  ledger: z.array(VoucherLedgerEntry),
}).meta({ id: 'VoucherWithLedger' });
export type VoucherWithLedger = z.infer<typeof VoucherWithLedger>;

/** Staff (till) balance lookup result. */
export const VoucherBalance = z
  .object({
    voucherId: Uuid,
    code: z.string(),
    kind: VoucherKind,
    purpose: VoucherPurpose,
    status: VoucherStatus,
    originalAmountPence: Pence,
    balancePence: Pence,
    expiresAt: IsoDateTime,
  })
  .meta({ id: 'VoucherBalance' });
export type VoucherBalance = z.infer<typeof VoucherBalance>;

/**
 * Client-facing balance lookup by code (no login; on the receipt). Deliberately omits all salon-
 * internal ids (voucherId, renterId) — only customer-facing fields.
 */
export const PublicVoucherBalance = z
  .object({
    code: z.string(),
    kind: VoucherKind,
    status: VoucherStatus,
    balancePence: Pence,
    expiresAt: IsoDateTime,
  })
  .meta({ id: 'PublicVoucherBalance' });
export type PublicVoucherBalance = z.infer<typeof PublicVoucherBalance>;

/**
 * The gift-card liability reconciliation report (ADR-0009 §"reconciliation invariant"). Takings reports
 * read `revenue` (+ vat_output) from the ledger event, NEVER gift_card_liability or the raw voucher_sale
 * gross. INVARIANT: sold − redeemed − expired = outstanding = −Σ(signed gift_card_liability), to the penny.
 */
export const VoucherLiabilityReport = z
  .object({
    soldPence: Pence.describe('Σ gift_card_liability credits from SALE + COMP issue events'),
    redeemedPence: Pence.describe('Σ gift_card_liability debits from REDEMPTION events'),
    expiredPence: Pence.describe('Σ gift_card_liability debits from EXPIRY/VOID events'),
    outstandingPence: Pence.describe(
      'the live liability = Σ(signed gift_card_liability) = credits − debits',
    ),
    /** revenue recognised at redemption (ex-VAT) + the output VAT split — for cross-check vs takings. */
    revenueRecognisedPence: Pence,
    vatOutputPence: Pence,
    reconciles: z.boolean().describe('sold − redeemed − expired === outstanding (must be true)'),
    activeVoucherCount: z.int().min(0),
    generatedAt: IsoDateTime,
  })
  .meta({ id: 'VoucherLiabilityReport' });
export type VoucherLiabilityReport = z.infer<typeof VoucherLiabilityReport>;

// ── Query schemas (NO .meta — entity-only carry .meta({ id })) ─────────────────

export const ListVouchersQuery = z.object({
  status: VoucherStatus.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ListVouchersQuery = z.infer<typeof ListVouchersQuery>;
